/**
 * Cross-tab signal for `GET /api/labor/open-session`. React Query caches are per tab;
 * `localStorage`, BroadcastChannel, and a same-tab custom event let other tabs refetch
 * (or apply optimistic SSE cache) after clock in or clock out.
 */
const STORAGE_KEY = "integrator_hub_labor_open_session_sync_v1";
const BROADCAST_CHANNEL_NAME = "sgs-hub-labor-open-session";

export const LABOR_OPEN_SESSION_SYNC_STORAGE_KEY = STORAGE_KEY;

export const LABOR_OPEN_SESSION_SYNC_EVENT = "integrator-hub-labor-open-session-sync";

let broadcastChannel: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (broadcastChannel == null) {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  }
  return broadcastChannel;
}

export function broadcastLaborOpenSessionChanged(): void {
  if (typeof window === "undefined") return;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  }
  window.dispatchEvent(new CustomEvent(LABOR_OPEN_SESSION_SYNC_EVENT));
  try {
    getBroadcastChannel()?.postMessage({ at: Date.now() });
  } catch {
    // BroadcastChannel can throw if the browsing context is closing.
  }
}

/** Subscribe to same-tab custom events, cross-tab storage, and BroadcastChannel. */
export function subscribeLaborOpenSessionSync(listener: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    listener();
  };
  const onBroadcast = (): void => {
    listener();
  };
  window.addEventListener(LABOR_OPEN_SESSION_SYNC_EVENT, listener);
  window.addEventListener("storage", onStorage);
  const channel = getBroadcastChannel();
  channel?.addEventListener("message", onBroadcast);
  return () => {
    window.removeEventListener(LABOR_OPEN_SESSION_SYNC_EVENT, listener);
    window.removeEventListener("storage", onStorage);
    channel?.removeEventListener("message", onBroadcast);
  };
}
